/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, Upload, X, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export function parseApiQr(text) {
  if (!text || typeof text !== "string") return null;
  const t = text.trim();
  const norm = (obj) => {
    const lower = {};
    for (const [k, v] of Object.entries(obj || {})) {
      lower[String(k).toLowerCase().replace(/[-_\s]/g, "")] = String(v ?? "");
    }
    const pick = (...keys) => {
      for (const k of keys) if (lower[k]) return lower[k];
      return "";
    };
    const api_key = pick("apikey", "key", "accesskey", "apikeyid");
    const api_secret = pick("secretkey", "secret", "apisecret", "secretaccesskey");
    const passphrase = pick("passphrase", "pass", "password");
    if (!api_key && !api_secret && !passphrase) return null;
    return { api_key, api_secret, passphrase };
  };
  try {
    const j = JSON.parse(t);
    if (j && typeof j === "object") { const r = norm(j); if (r) return r; }
  } catch { /* ignore */ }
  try {
    const u = t.includes("://") ? new URL(t) : null;
    if (u) {
      const obj = {}; u.searchParams.forEach((v, k) => { obj[k] = v; });
      const r = norm(obj); if (r) return r;
    }
  } catch { /* ignore */ }
  const kv = {};
  for (const line of t.split(/\r?\n|;/)) {
    const m = line.match(/^\s*([A-Za-z_][A-Za-z0-9_-]*)\s*[:=]\s*(.+?)\s*$/);
    if (m) kv[m[1]] = m[2];
  }
  const r = norm(kv);
  if (r) return r;
  return null;
}

export default function QRScanDialog({ open, onOpenChange, onResult }) {
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("camera"); // camera | upload
  const [cameraStarted, setCameraStarted] = useState(false); // wartet auf User-Klick (iOS-Gesture)
  const [errorInfo, setErrorInfo] = useState(null); // {title, hint}
  const [starting, setStarting] = useState(false);
  const scannerRef = useRef(null);
  const fileRef = useRef(null);
  const containerId = "qr-reader-container";

  const stopCamera = useCallback(async () => {
    const s = scannerRef.current;
    if (!s) return;
    scannerRef.current = null;
    try {
      // Nur stoppen wenn wirklich scannend — sonst wirft html5-qrcode
      if (s.isScanning) await s.stop();
    } catch { /* ignore */ }
    try { s.clear(); } catch { /* ignore */ }
  }, []);

  // Dialog-Reset bei Schließen
  useEffect(() => {
    if (!open) {
      stopCamera();
      setCameraStarted(false);
      setErrorInfo(null);
      setStarting(false);
    } else {
      // Beim Öffnen zurück auf Kamera-Modus (Standard)
      setMode("camera");
    }
  }, [open]);

  // Beim Wechsel weg von "camera" Kamera stoppen
  useEffect(() => {
    if (mode !== "camera") stopCamera();
  }, [mode]);

  const startCamera = useCallback(async () => {
    setErrorInfo(null);
    setStarting(true);
    // Sicherheits-Check: getUserMedia braucht HTTPS oder localhost
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setErrorInfo({
        title: "Kamera-API nicht verfügbar",
        hint: "Dein Browser unterstützt keine Kamera oder die Seite läuft nicht über HTTPS. Nutze Bild-Upload als Alternative.",
      });
      setStarting(false);
      return;
    }
    // Warten bis das Container-Element wirklich im DOM ist
    let el = document.getElementById(containerId);
    let tries = 0;
    while (!el && tries < 20) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => requestAnimationFrame(r));
      el = document.getElementById(containerId);
      tries += 1;
    }
    if (!el) {
      setErrorInfo({
        title: "Anzeigebereich für die Kamera fehlt",
        hint: "Bitte Dialog schließen und erneut öffnen.",
      });
      setStarting(false);
      return;
    }
    try {
      const scanner = new Html5Qrcode(containerId, { verbose: false });
      scannerRef.current = scanner;
      const config = { fps: 10, qrbox: { width: 240, height: 240 }, aspectRatio: 1.0 };
      // Reihenfolge: Rückkamera → Frontkamera → beliebige Kamera
      const attempts = [
        { facingMode: { exact: "environment" } },
        { facingMode: "environment" },
        { facingMode: "user" },
        true,
      ];
      let started = false;
      let lastErr = null;
      for (const camConfig of attempts) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await scanner.start(camConfig, config,
            (decodedText) => handleDecoded(decodedText),
            () => { /* frame-level errors ignorieren */ },
          );
          started = true;
          break;
        } catch (e) {
          lastErr = e;
          // Bei Permission-Fehler kein weiterer Versuch — Nutzer hat abgelehnt
          const msg = String(e?.message || e || "").toLowerCase();
          if (msg.includes("permission") || msg.includes("notallowed")) break;
        }
      }
      if (!started) throw lastErr || new Error("Kamera konnte nicht gestartet werden.");
      setCameraStarted(true);
    } catch (e) {
      const msg = String(e?.message || e || "");
      const low = msg.toLowerCase();
      let title = "Kamera konnte nicht gestartet werden";
      let hint = "Bitte Kamera-Berechtigung erlauben oder Bild-Upload nutzen.";
      if (low.includes("notallowed") || low.includes("permission")) {
        title = "Kamera-Zugriff wurde verweigert";
        hint = "Klick oben in der Browser-Adressleiste auf das Kamera-Symbol und erlaube den Zugriff, oder nutze stattdessen den Bild-Upload.";
      } else if (low.includes("notfound") || low.includes("no camera")) {
        title = "Keine Kamera gefunden";
        hint = "Auf diesem Gerät ist keine Kamera verfügbar. Nutze bitte den Bild-Upload.";
      } else if (low.includes("notreadable") || low.includes("trackstart")) {
        title = "Kamera wird bereits verwendet";
        hint = "Schließ andere Apps oder Browser-Tabs, die die Kamera nutzen, und versuch es erneut.";
      } else if (low.includes("secure") || low.includes("https")) {
        title = "HTTPS erforderlich";
        hint = "Kamera funktioniert nur auf https://-Seiten. Öffne die App über die sichere Adresse.";
      }
      setErrorInfo({ title, hint });
    } finally {
      setStarting(false);
    }
  }, []);

  const handleDecoded = (decodedText) => {
    if (busy) return;
    setBusy(true);
    const parsed = parseApiQr(decodedText);
    if (!parsed) {
      toast.error("QR-Code erkannt, aber Inhalt passte nicht", {
        description: "Der QR muss api_key, secret und passphrase enthalten (z. B. als JSON).",
      });
      setBusy(false);
      return;
    }
    toast.success("API-Daten erkannt", {
      description: "Felder wurden ausgefüllt — Passphrase bitte kurz prüfen.",
    });
    stopCamera();
    onResult?.(parsed);
    onOpenChange(false);
    setBusy(false);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const scanner = new Html5Qrcode(containerId, { verbose: false });
      const result = await scanner.scanFile(file, false);
      try { scanner.clear(); } catch { /* ignore */ }
      handleDecoded(result);
    } catch {
      toast.error("Kein QR-Code im Bild gefunden", {
        description: "Bitte scharfes Bild mit gutem Kontrast wählen.",
      });
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="qr-scan-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">API-Daten per QR-Code</DialogTitle>
          <DialogDescription>
            Zeigt dein Broker nach dem Erstellen eines API-Keys einen QR-Code? Scan ihn ein oder lade ein Screenshot davon hoch — wir füllen die Felder automatisch aus.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 border-b border-border pb-2">
          <button
            type="button"
            onClick={() => { setMode("camera"); setErrorInfo(null); }}
            className={`flex-1 rounded-lg py-2 text-sm inline-flex items-center justify-center gap-2 ${
              mode === "camera" ? "bg-primary/15 text-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid="qr-mode-camera"
          >
            <Camera className="h-4 w-4" strokeWidth={1.5} /> Kamera
          </button>
          <button
            type="button"
            onClick={() => setMode("upload")}
            className={`flex-1 rounded-lg py-2 text-sm inline-flex items-center justify-center gap-2 ${
              mode === "upload" ? "bg-primary/15 text-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid="qr-mode-upload"
          >
            <Upload className="h-4 w-4" strokeWidth={1.5} /> Bild-Upload
          </button>
        </div>

        <div className="min-h-[280px]">
          {mode === "camera" && (
            <>
              {/* Container muss IMMER gemountet sein, sobald Modus=camera — sonst race */}
              <div
                id={containerId}
                className={`rounded-xl overflow-hidden border border-border bg-black/40 ${cameraStarted ? "" : "hidden"}`}
                style={{ minHeight: cameraStarted ? 280 : 0 }}
              />
              {!cameraStarted && !errorInfo && (
                <div className="rounded-xl border border-dashed border-border p-8 text-center">
                  <Camera className="mx-auto h-6 w-6 text-muted-foreground" strokeWidth={1.5} />
                  <p className="text-sm text-muted-foreground mt-2 max-w-xs mx-auto">
                    Tippe auf „Kamera starten", damit dein Browser nach der Berechtigung fragen kann.
                  </p>
                  <Button
                    type="button"
                    className="rounded-full mt-4"
                    onClick={startCamera}
                    disabled={starting}
                    data-testid="qr-camera-start"
                  >
                    {starting ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} /> Kamera wird gestartet…</>
                    ) : (
                      <><Camera className="mr-2 h-4 w-4" strokeWidth={1.5} /> Kamera starten</>
                    )}
                  </Button>
                </div>
              )}
              {errorInfo && (
                <div className="rounded-xl border border-[hsl(0_66%_50%/0.4)] bg-[hsl(0_66%_50%/0.08)] p-5" data-testid="qr-camera-error">
                  <div className="flex items-start gap-3">
                    <ShieldAlert className="h-5 w-5 text-[hsl(0_66%_72%)] mt-0.5 shrink-0" strokeWidth={1.5} />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-[hsl(0_66%_72%)]">{errorInfo.title}</div>
                      <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{errorInfo.hint}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" className="rounded-full h-8" onClick={startCamera} data-testid="qr-camera-retry">
                          Erneut versuchen
                        </Button>
                        <Button size="sm" variant="ghost" className="rounded-full h-8" onClick={() => setMode("upload")}>
                          Zum Bild-Upload wechseln
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          {mode === "upload" && (
            <div className="rounded-xl border border-dashed border-border p-8 text-center">
              <Upload className="mx-auto h-6 w-6 text-muted-foreground" strokeWidth={1.5} />
              <p className="text-sm text-muted-foreground mt-2">
                Lade einen Screenshot oder ein Foto des QR-Codes hoch.
              </p>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onFile}
                data-testid="qr-upload-file"
              />
              <Button
                type="button"
                variant="outline"
                className="rounded-full mt-4"
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                data-testid="qr-upload-btn"
              >
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} /> : <Upload className="mr-2 h-4 w-4" strokeWidth={1.5} />}
                Bild auswählen
              </Button>
            </div>
          )}
        </div>

        <div className="text-[11px] text-muted-foreground leading-relaxed">
          Sicherheitshinweis: der QR-Inhalt wird ausschließlich im Browser verarbeitet — nichts wird an Dritte gesendet. Die extrahierten Felder kannst du vor dem Verbinden noch prüfen.
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="qr-cancel">
            <X className="mr-2 h-4 w-4" strokeWidth={1.5} /> Schließen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
